import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Trash2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, token, logout } = useAuth();
  const [newPhone, setNewPhone] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const navigate = useNavigate();

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/auth/update-phone`,
        { new_phone: newPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Phone number updated successfully!');
      setNewPhone('');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!window.confirm('Are you absolutely sure? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account deleted');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Settings
            </h1>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Profile Info */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="mt-1" />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input value={user?.verified_phone_number || ''} disabled className="mt-1" />
            </div>
          </CardContent>
        </Card>

        {/* Update Phone */}
        <Card>
          <CardHeader>
            <CardTitle>Update Phone Number</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdatePhone} className="space-y-4">
              <div>
                <Label htmlFor="new-phone">New Australian Phone Number</Label>
                <Input
                  id="new-phone"
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+61 4XX XXX XXX"
                  data-testid="new-phone-input"
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={updating || !newPhone}
                data-testid="update-phone-button"
              >
                {updating ? 'Updating...' : 'Update Phone Number'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Maintenance */}
        <Card>
          <CardHeader>
            <CardTitle>Database Maintenance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Clean up unused data to improve application performance.
            </p>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const response = await axios.post(
                    `${API_URL}/maintenance/cleanup-geocode-cache`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success(`Cleaned up ${response.data.deleted_count} unused geocode entries`);
                } catch (error) {
                  toast.error('Failed to cleanup cache');
                }
              }}
              data-testid="cleanup-cache-button"
            >
              Clean Up Geocode Cache
            </Button>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Delete Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              This will permanently delete your account, all trips, participants, and related data. This action cannot be undone.
            </p>
            <div>
              <Label htmlFor="delete-confirm">Type DELETE to confirm</Label>
              <Input
                id="delete-confirm"
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                data-testid="delete-confirm-input"
                className="mt-1"
              />
            </div>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirm !== 'DELETE'}
              data-testid="delete-account-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account Permanently
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 w-full py-4 text-center bg-white/80 backdrop-blur border-t border-slate-200">
        <a
          href="https://donate.stripe.com/5kQ6oH99f72ze6OcIK4ZG00"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Support this project
        </a>
      </footer>
    </div>
  );
}
